import { InputBase, InputAdornment, IconButton } from '@ori-ui/material-ui';
import useTestId from '@ori/testid-generator';
import bemCssModules from 'bem-css-modules';
import { observer } from 'mobx-react-lite';
import type { ChangeEvent, VFC } from 'react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Redirect, useHistory } from 'react-router-dom';

import SearchInputModuleCss from './SearchInput.module.css';
import { useSearchInputAdornmentStyle } from './styles/useSearchInputAdornmentStyle';
import { useSearchInputStyle } from './styles/useSearchInputStyle';
import { OriIcon } from '~/components/common/OriIcon/OriIcon';
import {
  localizationFallbackItems,
  LocalizationItem,
  MAX_QUERY_LENGTH,
  SiteMap,
} from '~/constants';
import { useSearchResultStore } from '~/stores';
import { customTheme } from '~/theme/custom/theme';

const block = bemCssModules(SearchInputModuleCss);

interface SearchFormProps {
  search: string;
}

export const SearchInput: VFC = observer(() => {
  const [isRedirect] = useState(false);
  const { register, handleSubmit, reset } = useForm<SearchFormProps>();
  const { searchQuery, setSearchQuery, loadSearchResults, resetSearchResults } =
    useSearchResultStore();
  const [inputValue, setInputValue] = useState(searchQuery);

  const searchInputStyle = useSearchInputStyle(customTheme);
  const searchAdornmentStyle = useSearchInputAdornmentStyle();

  const intl = useIntl();
  const history = useHistory();
  const { getTestId } = useTestId();

  const onSubmit = async ({ search }: SearchFormProps) => {
    if (searchQuery === search || search.trim() === '') {
      return;
    }
    resetSearchResults();
    if (search && search.length <= MAX_QUERY_LENGTH) {
      await loadSearchResults(search);
      if (SiteMap.Search.To) {
        history.push(SiteMap.Search.To(search));
      }
    }
    setSearchQuery(search);
  };

  const onReset = () => {
    setInputValue('');
    reset();
  };

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length <= MAX_QUERY_LENGTH) {
      setInputValue(event.target.value);
    }
  }, []);

  if (isRedirect) {
    return <Redirect to="/search" />;
  }

  return (
    <form
      className={block()}
      data-testid={getTestId('top-banner-question-input')}
      onSubmit={handleSubmit(onSubmit)}
      onReset={onReset}
    >
      <InputBase
        autoFocus
        name="search"
        placeholder={intl.formatMessage({
          id: LocalizationItem.SearchInSupportCenter,
          defaultMessage: localizationFallbackItems[LocalizationItem.SearchInSupportCenter],
        })}
        inputRef={register}
        endAdornment={
          <InputAdornment
            position="end"
            classes={searchAdornmentStyle}
          >
            <IconButton
              aria-label="Clear search field"
              type="reset"
            >
              <OriIcon
                name="cross"
                size="23.3px"
                color="#ccc"
              />
            </IconButton>
          </InputAdornment>
        }
        startAdornment={
          <InputAdornment
            position="start"
            classes={searchAdornmentStyle}
            onClick={handleSubmit(onSubmit)}
          >
            <OriIcon
              name="search"
              size="23.3px"
            />
          </InputAdornment>
        }
        classes={searchInputStyle}
        value={inputValue}
        onChange={onChange}
      />
    </form>
  );
});
